import { useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useCallback } from 'react';

import { SimpleErrorResponse } from '@/models/Error';
import { publicApi } from '@/utils/api';
import { appBranchesPath } from '@/utils/publicApiRouteService';
import { queryClient } from '@/utils/query';

type UseAppBranchesProps = {
  appSlug?: string;
  /** Narrows the listed branches, e.g. to a search term. */
  keyword?: string;
  limit?: number;
};

type UseBranchesForFilterResponse = AxiosResponse<{ data?: { name: string }[] }>;

const useAppBranchesForFilter = ({ appSlug, keyword = '', limit = 10 }: UseAppBranchesProps) => {
  const queryKey = useCallback((keyword: string = '') => [appSlug, limit, keyword], [appSlug, limit]);
  const queryFn = useCallback(
    (keyword: string = '') =>
      () =>
        publicApi.get<{ data?: { name: string }[] }>(appBranchesPath(appSlug || '', keyword, limit)),
    [appSlug, limit],
  );

  const { data, isLoading } = useQuery<UseBranchesForFilterResponse, SimpleErrorResponse, string[]>({
    queryKey: queryKey(keyword),
    queryFn: queryFn(keyword),
    retry: false,
    select: (d) => d.data.data?.map((branch) => branch.name) || [],
  });

  const fetchQuery = useCallback(
    async (keyword: string) => {
      const response = await queryClient.fetchQuery({
        queryKey: queryKey(keyword),
        queryFn: queryFn(keyword),
        retry: false,
      });
      return response.data.data?.map((branch) => branch.name) || [];
    },
    [queryFn, queryKey],
  );

  return {
    data,
    fetchQuery,
    isLoading,
  };
};

export default useAppBranchesForFilter;
